import React from "react";
import { Stub } from "component-library";
import { EditIcon } from "assets";

import { ImageLocation, Photo } from "models";
import { dataTestIds } from "data-testids";

import { getImageSource, getImageUrl, ImageWidth } from "services/UrlBuilder";

import { Picture } from "components/common/Picture";

import "./MainPhoto.scss";

export interface IMainPhotoContent {
  photoLoadingError: string;
  viewMainPhoto: string;
  editMainPhoto: string;
  mainPhotoAlternativeText: string;
}

export interface IMainPhotoProps {
  showError: boolean;
  openPhoto: () => void;
  content: IMainPhotoContent;
  photo?: Photo;
  editPhoto?: () => void;
  showPhotoId?: boolean;
}

interface IMainPhotoState {
  isLoadFailed: boolean;
}

export default class MainPhoto extends React.Component<
  IMainPhotoProps,
  IMainPhotoState
> {
  public readonly state: Readonly<IMainPhotoState> = { isLoadFailed: false };

  public render() {
    const { showError, content } = this.props;
    const { isLoadFailed } = this.state;

    const hasError = showError || isLoadFailed;

    return (
      <div id="main-photo-preview" className="c-mainphoto">
        <div
          className="c-mainphoto__image"
          data-testid={dataTestIds.profile["Photo.Headshot.headshotPhoto"]}
        >
          {!hasError ? (
            this.renderPhoto()
          ) : (
            <Stub text={content.photoLoadingError} />
          )}
        </div>
      </div>
    );
  }

  private renderPhoto = () => {
    const credits = this.getPhotoCredits();
    const { content, editPhoto } = this.props;

    return (
      <React.Fragment>
        <a
          className="c-mainphoto__view"
          onClick={this.viewPhoto}
          onKeyPress={(e) => e.key === "Enter" && this.viewPhoto(e)}
          aria-label={content.viewMainPhoto + " " + credits}
          tabIndex={0}
        >
          {this.renderImage()}
        </a>
        {editPhoto && (
          <a
            id="main-photo-edit"
            className="c-mainphoto__edit"
            onClick={this.editPhoto}
            onKeyPress={this.editPhotoKeypress}
            title={content.editMainPhoto}
            aria-label={content.editMainPhoto}
            tabIndex={0}
            data-testid={dataTestIds.profile["Photo.Headshot.editIcon"]}
          >
            <div className="c-mainphoto__edit-icon flex items-center justify-center">
              <EditIcon width={30} height={30} />
            </div>
          </a>
        )}
        {credits && this.renderCreditOverlay(credits)}
        {this.renderPhotoId()}
      </React.Fragment>
    );
  };

  private renderPhotoId = () => {
    const { photo: { userFriendlyId } = {}, showPhotoId = false } = this.props;

    return (
      userFriendlyId &&
      showPhotoId && (
        <div
          className="c-mainphoto__photo-id-overlay"
          data-testid={dataTestIds.profile["Photo.Headshot.headshotId"]}
        >
          {userFriendlyId}
        </div>
      )
    );
  };

  private renderCreditOverlay = (credits: string) => (
    <div className="c-mainphoto__credit-overlay">{credits}</div>
  );

  private renderImage = () => {
    const {
      photo,
      content: { viewMainPhoto },
    } = this.props;

    if (!photo) {
      return null;
    }

    const imageSource = getImageSource(photo.url, ImageWidth.WidthXSmall);

    return (
      <Picture
        sourcesSettings={[imageSource]}
        src={getImageUrl(photo.url, ImageLocation.MainPhoto)}
        alt={viewMainPhoto}
        onError={this.handleImageError}
      />
    );
  };

  private getPhotoCredits = () => {
    const { photo: { photographer } = {} } = this.props;

    return photographer ? "© " + photographer : null;
  };

  private handleImageError = () => this.setState({ isLoadFailed: true });

  private viewPhoto = (
    event:
      | React.MouseEvent<HTMLAnchorElement>
      | React.KeyboardEvent<HTMLAnchorElement>
  ) => {
    event.preventDefault();

    return this.props.openPhoto();
  };

  private editPhotoKeypress = (
    event: React.KeyboardEvent<HTMLAnchorElement>
  ) => {
    if (event.key === " " || event.key === "Enter") {
      return this.editPhoto(event);
    }
  };

  private editPhoto = (
    event:
      | React.MouseEvent<HTMLAnchorElement>
      | React.KeyboardEvent<HTMLAnchorElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();

    return this.props.editPhoto && this.props.editPhoto();
  };
}
